// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import featureFlagHelper from './feature-flag-helper';
import { getJSONTagContent } from '@squareup/public-web-browser-helpers/meta-tag-helper';

// Activates Optimizely experiments using the Experiment Relay service
const optimizelyHelper = {
  ACTIVATE_ENDPOINT: '/1.0/experimentation/activate-experiment',
  AVT_COOKIE: '_savt',
  META_TAG_PREFIX: 'optly_',
  EXPERIMENTS_ACTIVATED_ON_CDN_META_TAG: 'optly_exps_activated_on_cdn',
  EXPERIMENTS_BUCKETED_META_TAG: 'optly_vars',
  EXPERIMENTS_RENDERED_META_TAG: 'optly_rendered',
  EXPERIMENTS_OVERRIDE_META_TAG: 'optly_override',
  ACTIVATE_OVERRIDE_QUERY_PARAM: 'activate',
  ATTRIBUTES_META_TAG: 'optly_attrs',
  XMS_PRODUCTION_OPTIMIZELY_PROJECT_ID: '17891232017',
  HOSTS_BY_ENV: {
    // TODO: set up experiment relay staging w/ MarTech Test Project SDK key
    development: 'https://squareup.com',
    staging: 'https://squareup.com',
    production: 'https://squareup.com',
  },
  HOLDOUT_VARIATIONS_TO_ACTIVATE: ['holdout', 'symmetric_treatment'],
  env: 'staging', // default to staging, will be overriden in getEnv()
  host: 'https://squareupstaging.com', // default to staging
  permissions: {} as InitializationProperties,

  initialize(permissions: InitializationProperties) {
    optimizelyHelper.permissions = permissions;
    optimizelyHelper.init();
    window.Sq.Optimizely = this;
  },

  init() {
    if (featureFlagHelper.get('enable_optimizely_fullstack')) {
      optimizelyHelper.getEnv();
      optimizelyHelper.getHost();

      if (optimizelyHelper.env === 'development') {
        optimizelyHelper.setDummyExperiments();
      }

      const urlParams = optimizelyHelper.parseUrlParams(window.location.search);
      let manualActivate = false;

      if (urlParams.get(optimizelyHelper.ACTIVATE_OVERRIDE_QUERY_PARAM) === 'true') {
        manualActivate = true;
        console.log('Manual override - activating experiments.');
      }

      if (urlParams.has(optimizelyHelper.EXPERIMENTS_OVERRIDE_META_TAG) && !manualActivate) {
        console.log('Manual override - not activating experiments.');
        return;
      }

      const experiments = optimizelyHelper.getExperimentsToActivate();
      optimizelyHelper.enqueueExperiments(experiments);
    }
  },

  getAVT() {
    return optimizelyHelper.getCookie(optimizelyHelper.AVT_COOKIE);
  },

  getCookie(name) {
    const v = document.cookie.match(`(^|;) ?${name}=([^;]*)(;|$)`);
    return v ? decodeURIComponent(v[2]) : null;
  },

  parseUrlParams(params) {
    return new URLSearchParams(params);
  },

  // Returns an array of experiment objects to be activated with
  // [name] and [variation] attributes
  getExperimentsToActivate() {
    const experiments = [];
    const experimentsMetaTag = document.querySelector(`meta[name="${optimizelyHelper.EXPERIMENTS_RENDERED_META_TAG}"]`);

    if (!experimentsMetaTag) {
      return [];
    }

    const experimentsList = experimentsMetaTag.getAttribute('content');
    if (!optimizelyHelper.validExperimentList(experimentsList)) {
      return [];
    }

    const activatedOnCDN = new Set();
    const activatedOnCDNMetaTag = document.querySelector(
      `meta[name="${optimizelyHelper.EXPERIMENTS_ACTIVATED_ON_CDN_META_TAG}"]`
    );
    const cdnExperiments = activatedOnCDNMetaTag ? activatedOnCDNMetaTag.getAttribute('content') : '';
    cdnExperiments.split(';').forEach((experiment) => {
      const [name] = experiment.split(':');
      activatedOnCDN.add(name);
    });

    experimentsList.split(';').forEach((experiment) => {
      const [name, variation] = experiment.split(':');
      // do not activate experiments that have already been activated on the CDN
      if (!activatedOnCDN.has(name)) {
        experiments.push({ name, variation });
      }
    });

    const optlyVars = this.getBucketName();
    const holdoutVariationString = optlyVars.split(';').find((experiment) => experiment.includes('holdout'));
    if (holdoutVariationString) {
      const [name, variation] = holdoutVariationString.split(':');

      if (!activatedOnCDN.has(name) && optimizelyHelper.HOLDOUT_VARIATIONS_TO_ACTIVATE.includes(variation)) {
        experiments.push({ name, variation });
      }
    }

    return experiments;
  },

  // Validates whether the experiment list is in the following format:
  // <name>:<variation>;<name>:<variation>
  validExperimentList(experimentsList) {
    const pattern = new RegExp('^(([^;:]+:[^;:]+)(;|$))+$');
    return pattern.test(experimentsList);
  },

  // Returns bucket name (optly_vars)
  getBucketName() {
    const bucketMetaTag = document.querySelector(`meta[name="${optimizelyHelper.EXPERIMENTS_BUCKETED_META_TAG}"]`);
    if (!bucketMetaTag) {
      return '';
    }
    return bucketMetaTag.getAttribute('content');
  },

  // Returns an attributes object
  getAttributes() {
    const attributesMetaTag = document.querySelector(`meta[name="${optimizelyHelper.ATTRIBUTES_META_TAG}"]`);
    if (!attributesMetaTag) {
      return {};
    }

    const content = attributesMetaTag.getAttribute('content');
    try {
      return JSON.parse(content);
    } catch {
      return content;
    }
  },

  // Return all optly_* metadata tags as one object
  getAllMetadata() {
    const matches = document.querySelectorAll(`meta[name^="${optimizelyHelper.META_TAG_PREFIX}"]`);

    if (matches.length === 0) {
      return undefined;
    }

    const metas = {};
    matches.forEach((meta) => {
      const name = meta.getAttribute('name');
      const content = meta.getAttribute('content');

      // try to parse the content
      try {
        const parsedContent = JSON.parse(content);
        metas[name] = parsedContent;
      } catch {
        metas[name] = content;
      }
    });

    return metas;
  },

  getEnv() {
    const [error, envMetaTag] = getJSONTagContent<EnvironmentConfig>('sq:environment');
    if (error !== null) {
      console.warn('sq:environment meta tag cannot be found or has invalid content.', error);
      return;
    }
    optimizelyHelper.env = envMetaTag.env;
  },

  getHost() {
    if (!optimizelyHelper.env) {
      optimizelyHelper.getEnv();
    }
    optimizelyHelper.host = optimizelyHelper.HOSTS_BY_ENV[optimizelyHelper.env];
  },

  // Appends meta tags with dummy experiment data for local development
  setDummyExperiments() {
    const varsMetaTag = document.createElement('meta');
    varsMetaTag.name = optimizelyHelper.EXPERIMENTS_BUCKETED_META_TAG;
    varsMetaTag.content = 'experiment:variation;cdn_experiment:variation';

    const renderedMetaTag = document.createElement('meta');
    renderedMetaTag.name = optimizelyHelper.EXPERIMENTS_RENDERED_META_TAG;
    renderedMetaTag.content = 'experiment:variation;cdn_experiment:variation';

    const attributesMetaTag = document.createElement('meta');
    attributesMetaTag.name = optimizelyHelper.ATTRIBUTES_META_TAG;
    attributesMetaTag.content = `${JSON.stringify({ attr: 'value' })}`;

    const activatedOnCDNMetaTag = document.createElement('meta');
    activatedOnCDNMetaTag.name = optimizelyHelper.EXPERIMENTS_ACTIVATED_ON_CDN_META_TAG;
    activatedOnCDNMetaTag.content = 'cdn_experiment:variation';

    const head = document.getElementsByTagName('head')[0];
    head.appendChild(varsMetaTag);
    head.appendChild(renderedMetaTag);
    head.appendChild(attributesMetaTag);
    head.appendChild(activatedOnCDNMetaTag);
  },

  logToCDP(event, props) {
    window.Sq.Tracking.track(
      {
        action: event,
        properties: props,
      },
      { destinations: ['cdp'] }
    );
  },

  enqueueExperiments(experiments) {
    window.Sq.Tracking.subscribe('sq:oneTrustInitialized', (ev: CustomEvent<Sq.Tracking.TrackingPermissions>) => {
      if (ev.detail.functionalityTrackingEnabled) {
        optimizelyHelper.activateExperiments(experiments);
      }
    });
  },

  activateExperiments(experiments) {
    const attributes = optimizelyHelper.getAttributes();
    const avt = optimizelyHelper.getAVT();

    experiments.forEach((experiment) => {
      optimizelyHelper.activate(experiment.name, experiment.variation, avt, attributes);
    });
  },

  activate(experimentName, variation, avt, attributes) {
    const formattedAttributes = [];
    for (const [name, value] of Object.entries(attributes)) {
      const attribute = optimizelyHelper.formatAttribute(name, value);
      formattedAttributes.push(attribute);
    }

    const raw = JSON.stringify({
      experiment_name: experimentName,
      variant_name: variation,
      user_token: {
        token: avt,
        type: 'ANONYMOUS_VISITOR',
      },
      user_attributes: {
        user_attributes: formattedAttributes,
      },
      project_id: optimizelyHelper.XMS_PRODUCTION_OPTIMIZELY_PROJECT_ID,
    });

    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: raw,
      redirect: 'follow',
    };

    optimizelyHelper.logToCDP('Optimizely Activation Initiated', {
      requestOptions,
    });

    fetch(`${optimizelyHelper.host}${optimizelyHelper.ACTIVATE_ENDPOINT}`, requestOptions)
      .then((response) => response.json())
      .then((responseJson) => {
        optimizelyHelper.logToCDP('Optimizely Activation Response', {
          requestOptions,
          responseJson,
        });
      })
      .catch((error) => {
        console.error(error);

        optimizelyHelper.logToCDP('Optimizely Activation Error', {
          requestOptions,
          error: error.toString(),
          errorStack: error.stack,
        });
      });
  },

  // Formats an attribute based on the attribute's data type
  // Throws errors if the attribute is not one of the supported data types
  // (string, bool, int, double)
  formatAttribute(name, value) {
    switch (typeof value) {
      case 'string': {
        return { name, string_value: value };
      }
      case 'boolean': {
        return { name, bool_value: value };
      }
      case 'number': {
        // from https://stackoverflow.com/a/14636725
        const remainder = value % 1;
        if (remainder === 0) {
          // integer
          return { name, int_value: value };
        } else if (Number.isNaN(remainder)) {
          // NaN, Infinity, or -Infinity
          throw new TypeError(this.attributeErrorMessage(name, value));
        } else {
          // float
          return { name, double_value: value };
        }
      }
      default: {
        throw new TypeError(this.attributeErrorMessage(name, value));
      }
    }
  },

  attributeErrorMessage(name, value) {
    return `Optimizely attribute ${name} has unsupported data type ${typeof value}`;
  },
};

export default optimizelyHelper;
